import { useEffect, useState } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import type { Consideration, DismissReason, SessionPlan, PlannedExercise, OverviewSnapshot, Challenge } from "@coachbrain/contracts";
import { adaptationLabels } from "@coachbrain/contracts";
import { Card, Chip, ConfidenceDots, LayerChip, PrimaryButton, SectionLabel, TapAnswers, Divider, Num, Columns, Col } from "@coachbrain/ui";
import type { CoachBrainApi } from "../api";
import { CoachShell, ErrorBox, Loading, useDisplayName, useMedia } from "../components/common";
import { useLoad, useUnits } from "../lib/hooks";
import { dateDayMonth, prescriptionText } from "../lib/format";
import { en } from "../i18n/en";
import { DismissSheet } from "./challenges";

const t = en.overview;

function PlanExercise({ ex, units }: { ex: PlannedExercise; units: "lb" | "kg" }) {
  const p = ex.prescription;
  return (
    <div className="cb-row" style={{ gap: 6 }}>
      <div className="cb-row-main"><span className="cb-row-left">{ex.exerciseText}</span><Num className="cb-row-right">{prescriptionText(p, units)}</Num></div>
      {ex.successCriterion ? <div className="cb-row-sub">{t.success} {ex.successCriterion}</div> : null}
      {p.ceilingNote ? <div className="cb-callout-unresolved">{p.ceilingNote}</div> : null}
      {ex.alternatives.length ? <div className="cb-inline-actions">{ex.alternatives.map((a) => <Chip key={a.exerciseText} tone="measured">{t.alt} {a.exerciseText}</Chip>)}</div> : null}
    </div>
  );
}

function planIntent(plan: SessionPlan) {
  const intent = `${adaptationLabels[plan.intent.primary]}${plan.intent.secondary ? " + " + (plan.intent.secondary === "strength" && plan.blocks.some((b) => b.exercises.some((e) => e.pattern === "hinge")) ? "hinge" : adaptationLabels[plan.intent.secondary].toLowerCase()) : ""}`;
  return plan.title === "Tolerance + hinge" ? plan.title : intent;
}

/**
 * Today's session. On phones the alternative framework is one link away ("See that session"); from 1024px the
 * alternative renders SIDE BY SIDE inside the card (R-CLI-03) when `sideBySide` is supplied.
 */
export function PlanCard({ plan, units, onAdopt, onEdit, onSeeAlternative, adoptedLabel, sideBySide, onUseAlternative }: { plan: SessionPlan; units: "lb" | "kg"; onAdopt?: () => void; onEdit?: () => void; onSeeAlternative?: () => void; adoptedLabel?: string; sideBySide?: SessionPlan | null; onUseAlternative?: () => void }) {
  const exercises = (p: SessionPlan) => p.blocks.flatMap((b) => b.exercises).map((ex) => <PlanExercise key={ex.id} ex={ex} units={units} />);
  return (
    <Card className="cb-card-flush">
      <div className="cb-row-main"><span className="cb-h3">{t.todaysSession}</span><span className="cb-small cb-strong">{planIntent(plan)}</span></div>
      {sideBySide ? (
        <div className="cb-plan-side">
          <div className="cb-card-flush"><div className="cb-plan-side-head">{t.recommended}</div>{exercises(plan)}</div>
          <div className="cb-card-flush"><div className="cb-plan-side-head">{t.anotherFramework(sideBySide.alternativeFramework.name)}</div>{exercises(sideBySide)}<div style={{ paddingTop: 10 }}><Chip tone="navy" onClick={onUseAlternative}>{t.useFramework}</Chip></div></div>
        </div>
      ) : exercises(plan)}
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <div className="cb-btn-row">
          <PrimaryButton tone="emerald" onClick={onAdopt} disabled={!!plan.adopted}>{plan.adopted ? (adoptedLabel ?? t.adopted) : t.adopt}</PrimaryButton>
          <PrimaryButton tone="outline" onClick={onEdit}>{t.edit}</PrimaryButton>
        </div>
        {!sideBySide ? <p className="cb-note">{plan.alternativeFramework.summary} <button type="button" className="cb-link-button" onClick={onSeeAlternative}>{t.seeThatSession}</button></p> : <p className="cb-note">{plan.alternativeFramework.summary}</p>}
      </div>
    </Card>
  );
}

function ConsiderationCard({ c, onDismiss, onDraft, onSnooze }: { c: Consideration; onDismiss: (r: DismissReason | null) => void; onDraft: () => void; onSnooze: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <Card accent="unresolved" style={{ gap: 4 }}>
      <button type="button" style={{ all: "unset", cursor: "pointer", display: "block" }} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        <div className="cb-h3">{c.noticed}</div>
        <div className="cb-row-sub" style={{ color: "var(--cb-text)" }}>{c.whyItMatters}</div>
      </button>
      {open ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 6 }}>
          <div className="cb-inline-actions">
            <Chip tone="navy" onClick={onDraft}>{t.draftSession}</Chip>
            <Chip tone="ground" onClick={onSnooze}>{t.snooze}</Chip>
          </div>
          <SectionLabel>{t.dismissReason}</SectionLabel>
          <div className="cb-inline-actions">
            {(Object.keys(t.reasons) as DismissReason[]).map((r) => <Chip key={r} tone="ground" onClick={() => onDismiss(r === "other" ? null : r)}>{t.reasons[r]}</Chip>)}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

/** Compact challenge cards for the always-visible desktop column (>= 1024px). */
function ChallengeColumn({ api, clientId, onChange }: { api: CoachBrainApi; clientId: string; onChange: () => Promise<void> }) {
  const { data, setData } = useLoad((a) => a.challenges(clientId), [clientId]);
  const [dismissing, setDismissing] = useState<string | null>(null);
  const navigate = useNavigate();
  if (!data) return <Loading />;
  const refresh = async () => { setData(await api.challenges(clientId)); await onChange(); };
  const tc = en.challenges; const th = en.history;
  return (
    <>
      <SectionLabel className="cb-page-label" right={<button type="button" className="cb-link-button" onClick={() => void navigate({ to: `/clients/${clientId}/challenges` })}>{tc.title}</button>}>{tc.open} · {data.open.length}</SectionLabel>
      {data.open.length === 0 ? <Card><span className="cb-muted">{tc.none}</span></Card> : null}
      {data.open.map((c: Challenge, i: number) => (
        <Card key={c.id} tone="provisional">
          <SectionLabel tone="violet" right={<LayerChip layer={c.layer} />}>{th.challenge(i + 1, data.open.length)}</SectionLabel>
          <div className="cb-h3">{c.questions}</div>
          <div className="cb-small cb-text-violet">{th.settle} {c.settles}</div>
          <div className="cb-inline-actions">
            <Chip tone="navy" onClick={async () => { await api.challengeAddToToday(c.id); await refresh(); }}>{th.addToToday}</Chip>
            <Chip tone="ground" onClick={async () => { await api.challengeAskClient(c.id); await refresh(); }}>{th.ask("Fred")}</Chip>
            <Chip tone="ground" onClick={() => setDismissing(c.id)}>{th.dismiss}</Chip>
          </div>
        </Card>
      ))}
      {dismissing ? <DismissSheet onClose={() => setDismissing(null)} onPick={async (r) => { await api.dismissChallenge(dismissing, r ? { reason: r } : null); setDismissing(null); await refresh(); }} /> : null}
    </>
  );
}

/**
 * The client overview body (R-OVR-01 order). Rendered by the overview route and, from 768px, inside the
 * Clients master/detail pane (`embedded` adds a pane header because the top bar then says "Clients").
 */
export function OverviewBody({ id, embedded, onHeader }: { id: string; embedded?: boolean; onHeader?: (h: OverviewSnapshot["header"]) => void }) {
  const navigate = useNavigate();
  const units = useUnits();
  const { api, data, error, reload, setData } = useLoad((a) => a.overview(id), [id]);
  const [showAllCons, setShowAllCons] = useState(false);
  const [alt, setAlt] = useState<SessionPlan | null>(null);
  const [sideAlt, setSideAlt] = useState<SessionPlan | null>(null);
  const wide = useMedia("(min-width: 1024px)");
  const sideBySideOk = useMedia("(min-width: 1440px)");
  const planId = data?.todaysSession?.id;
  useEffect(() => { if (data && onHeader) onHeader(data.header); }, [data, onHeader]);
  useEffect(() => {
    let on = true;
    if (wide && !embedded && api && planId) void api.alternativePlan(planId).then((p) => { if (on) setSideAlt(p); }).catch(() => { if (on) setSideAlt(null); });
    else setSideAlt(null);
    return () => { on = false; };
  }, [wide, api, planId]);
  if (error) return <ErrorBox message={error} onRetry={reload} />;
  if (!data || !api) return <Loading />;
  const o: OverviewSnapshot = data;
  const primary = o.whatMattersNow.find((h) => h.rank === "primary");
  const secondaries = o.whatMattersNow.filter((h) => h.rank === "secondary");
  const q = o.openQuestions[0];
  const plan = alt ?? o.todaysSession;
  const goalWords = o.goal.goal.verbatim;
  const cons = showAllCons ? o.considerations.visible : o.considerations.visible.slice(0, 2);
  const late = o.goal.checkInStatus === "overdue" ? t.checkInLate(o.goal.overdueDays ?? 0) : o.goal.checkInStatus === "due" ? t.checkInDue : null;
  const refresh = async () => setData(await api.overview(id));

  return (
    <>
      {embedded ? (
        <div className="cb-pane-head">
          <div><div className="cb-pane-title">{o.header.name}</div><div className="cb-pane-sub">{o.header.frequencyText} · {o.header.dateText}</div></div>
          <button type="button" className="cb-pill-challenges" onClick={() => void navigate({ to: `/clients/${id}/challenges` })} aria-label={`${o.header.challengeCount} challenges`}>{t.challenges} <b>{o.header.challengeCount}</b></button>
        </div>
      ) : null}
      <Columns kind="overview">
        <Col area="left">
          {o.intakeNudge ? <Card tone="measured"><span className="cb-small">{o.intakeNudge}</span></Card> : null}
          <Card tone="goal">
            <SectionLabel tone="emerald" right={late ? <span className="cb-badge cb-badge-unresolved">{late}</span> : null}>{t.goalHisWords}</SectionLabel>
            <div className="cb-h2">“{goalWords}”</div>
            <div className="cb-row-sub" style={{ color: "var(--cb-text)" }}>{o.goal.goal.horizon === "long" ? t.longTermSet(dateDayMonth(o.goal.goal.setAt)) : t.shortTermSet(dateDayMonth(o.goal.goal.setAt))}{o.goal.strategyIntent ? ` · ${o.goal.strategyIntent}` : ""}</div>
          </Card>
          {primary ? (
            <Card>
              <div className="cb-card-head-provisional"><SectionLabel tone="violet" right={<LayerChip layer="interpretation" />}>{t.whatMattersNow}</SectionLabel></div>
              <div className="cb-row-main"><span className="cb-h3" style={{ fontSize: 19 }}>{primary.adaptation === "tissue_tolerance" ? "Knee tissue tolerance" : adaptationLabels[primary.adaptation]}</span><span className="cb-section-label">{t.primary}</span></div>
              <ConfidenceDots level={primary.confidence} />
              <div className="cb-row-sub" style={{ color: "var(--cb-text)" }}>{primary.evidenceSummary}</div>
              {secondaries.map((h) => (
                <div key={h.id}>
                  <Divider />
                  <div className="cb-row-main"><span className="cb-h3">{adaptationLabels[h.adaptation]}</span><span className="cb-small cb-strong cb-text-violet">{t.secondary} · {h.confidence === "low" ? t.thinEvidence : h.confidence}</span></div>
                </div>
              ))}
            </Card>
          ) : null}
          {o.recently.length ? (
            <Card style={{ gap: 4 }}>
              <SectionLabel>{t.recently}</SectionLabel>
              {o.recently.map((r) => <div key={r.id} className={`cb-small ${r.layer === "signal" ? "cb-muted" : ""}`}>{r.text}</div>)}
            </Card>
          ) : null}
          {q ? (
            <Card tone="unresolved">
              <SectionLabel tone="unresolved">{o.openQuestions.length > 1 ? t.openQuestions(o.openQuestions.length) : t.oneOpenQuestion}</SectionLabel>
              <div className="cb-h2">{q.text}</div>
              <div className="cb-body"><strong>{t.ifNobodyAnswers}</strong> {q.defaultIfUnanswered}.</div>
              <TapAnswers options={q.options ?? ["Yes", "No"]} onPick={async (o2) => { await api.answerQuestion(q.id, o2); await refresh(); }} />
            </Card>
          ) : null}
        </Col>
        <Col area="right">
          {plan ? (
            <PlanCard plan={plan} units={units}
              sideBySide={sideBySideOk && !embedded && !alt ? sideAlt : null}
              onUseAlternative={async () => { await api.swapPlan(o.todaysSession!.id, "framework"); await refresh(); }}
              onAdopt={async () => { await api.adoptPlan(plan.id); await refresh(); setAlt(null); }}
              onEdit={() => void navigate({ to: `/clients/${id}/plan/${plan.id}/edit` })}
              onSeeAlternative={async () => { if (alt) { setAlt(null); return; } setAlt(await api.alternativePlan(plan.id)); }}
            />
          ) : null}
          {alt ? <div className="cb-inline-actions"><Chip tone="navy" onClick={async () => { await api.swapPlan(o.todaysSession!.id, "framework"); setAlt(null); await refresh(); }}>{t.useFramework}</Chip><Chip tone="ground" onClick={() => setAlt(null)}>{t.backToDefault}</Chip></div> : null}
          <SectionLabel className="cb-page-label" right={<button type="button" className="cb-link-button" style={{ color: "var(--cb-unresolved-text)" }} onClick={() => setShowAllCons((s) => !s)}>{t.ofN(cons.length, o.considerations.total)}</button>}>{t.considerations}</SectionLabel>
          {cons.map((c) => (
            <ConsiderationCard key={c.id} c={c}
              onDismiss={async (r) => { await api.dismissConsideration(c.id, r ? { reason: r } : null); await refresh(); }}
              onDraft={async () => { const d = await api.draftFromConsideration(c.id); setAlt(d); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              onSnooze={async () => { await api.snoozeConsideration(c.id, new Date(Date.now() + 7 * 864e5).toISOString()); await refresh(); }}
            />
          ))}
          {!showAllCons && o.considerations.total > cons.length ? <button type="button" className="cb-link-button" onClick={() => setShowAllCons(true)}>{t.seeAll} ({o.considerations.total})</button> : null}
          <div className="cb-inline-actions" style={{ marginTop: 6 }}>
            <Chip tone="navy" onClick={() => void navigate({ to: `/clients/${id}/log` })}>{t.logSession}</Chip>
            <Chip tone="ground" onClick={() => void navigate({ to: `/clients/${id}/history` })}>{t.history}</Chip>
            <Chip tone="ground" onClick={() => void navigate({ to: `/clients/${id}/reports` })}>{t.reports}</Chip>
            <Chip tone="ground" onClick={() => void navigate({ to: `/clients/${id}/reviews` })}>{t.reviews}</Chip>
          </div>
        </Col>
        {wide && !embedded ? <Col area="aside"><ChallengeColumn api={api} clientId={id} onChange={refresh} /></Col> : null}
      </Columns>
    </>
  );
}

export function OverviewRoute() {
  const who = useDisplayName();
  const { id } = useParams({ strict: false }) as { id: string };
  const navigate = useNavigate();
  const [header, setHeader] = useState<OverviewSnapshot["header"] | null>(null);
  return (
    <CoachShell
      title={header?.name ?? " "}
      subtitle={header ? `${header.frequencyText} · ${header.dateText}` : undefined}
      right={<><span className="cb-phone-only">{who}</span>{header ? <span className="cb-desktop-only"><button type="button" className="cb-pill-challenges" onClick={() => void navigate({ to: `/clients/${id}/challenges` })} aria-label={`${header.challengeCount} challenges`}>{t.challenges} <b>{header.challengeCount}</b></button></span> : null}</>}
      tabs={header ? <div className="cb-phone-only cb-pill-row"><button type="button" className="cb-pill-challenges" onClick={() => void navigate({ to: `/clients/${id}/challenges` })} aria-label={`${header.challengeCount} challenges`}>{t.challenges} <b>{header.challengeCount}</b></button></div> : undefined}
    >
      <OverviewBody id={id} onHeader={setHeader} />
    </CoachShell>
  );
}
